import {
  Card,
  IconButton,
  Icon,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Switch,
  Typography,
  Chip,
  Alert
} from "@mui/material";
import { useState, useEffect } from "react";
import DataTable from "../../../../Components/Table/DataTable";
import WDBox from "../../../../MaterialComponents/WDBox";
import TableLoader from "../../../../Components/AppLoader/TableLoader";
import AppService from "../../../../Configurations/Appservice";
import moment from "moment";
import { useDispatch } from "react-redux";
import { updateScreenTitle } from "../../../../LayoutContainers/AppLayout/redux/AppSlice";
import { useNavigate } from "react-router-dom";
import useChannelPartner from "../../../../hooks/useChannelPartner";

const tableInitialData = {
  columns: [
    { Header: "Organization Name", accessor: "name", width: "22%" },
    { Header: "Commission", accessor: "commission", width: "18%" },
    { Header: "KYC Status", accessor: "kycStatus", width: "12%" },
    { Header: "Created Date", accessor: "createdAt", width: "15%" },
    { Header: "Status", accessor: "status", width: "15%" },
    { Header: "Actions", accessor: "actions", width: "10%" },
  ],

  rows: [],
};

const ChannelPartnerList = () => {
  const [tableData, setTableData] = useState(tableInitialData);
  const [isLoading, setIsLoading] = useState(true);
  const [statusChanging, setStatusChanging] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Use channel partner hook for API calls
  const {
    applications,
    loading,
    error,
    fetchApplications
  } = useChannelPartner();

  useEffect(() => {
    dispatch(updateScreenTitle("Channel Partners"));
    return () => dispatch(updateScreenTitle(""));
  }, [dispatch]);

  // Dialog state for status change
  const [statusDialog, setStatusDialog] = useState({
    open: false,
    partnerId: null,
    newStatus: false
  });

  const handleStatusChange = async (partnerId, newStatus) => {
    setStatusChanging(true);
    try {
      // TODO: Implement status change API call when backend endpoint is available
      // For now, just update UI and log
      console.log('Status change requested for partner:', partnerId, 'New status:', newStatus);

      // Update UI to reflect the change
      setTableData((prev) => {
        const updatedRows = prev.rows.map((row) => {
          if (row.id === partnerId) {
            return {
              ...row,
              status: (
                <>
                  <span>{newStatus ? "Active" : "Inactive"}</span>
                  <Switch
                    checked={newStatus}
                    color={newStatus ? "success" : "error"}
                    onChange={() => {
                      setStatusDialog({ open: true, partnerId: partnerId, newStatus: !newStatus });
                    }}
                    style={{ marginLeft: 8 }}
                  />
                </>
              )
            };
          }
          return row;
        });
        return { ...prev, rows: updatedRows };
      });
    } catch (err) {
      console.error('Failed to change partner status:', err);
    } finally {
      setStatusChanging(false);
    }
  };

  const _generateTableRows = (partners) => {
    const rows = partners.map((partner) => {
      const isActive = partner.status === 'ACTIVE';
      const commissionPercentage = partner.commissionPercentage || 0;
      const commissionType = partner.commissionType || 'PROFIT';

      // Status color mapping
      const getStatusColor = (status) => {
        switch (status) {
          case 'ACTIVE': return { bg: '#4caf50', color: 'white' };
          case 'PENDING': return { bg: '#ff9800', color: 'white' };
          case 'REJECTED': return { bg: '#f44336', color: 'white' };
          case 'SUSPENDED': return { bg: '#9e9e9e', color: 'white' };
          default: return { bg: '#e0e0e0', color: 'black' };
        }
      };

      const getKycStatusColor = (status) => {
        switch (status) {
          case 'VERIFIED': return { bg: '#4caf50', color: 'white' };
          case 'PENDING': return { bg: '#ff9800', color: 'white' };
          case 'REJECTED': return { bg: '#f44336', color: 'white' };
          default: return { bg: '#e0e0e0', color: 'black' };
        }
      };

      const statusColors = getStatusColor(partner.status);
      const kycColors = getKycStatusColor(partner.kycStatus);

      return {
        id: partner.id,
        organisationId: partner.organisationId,
        name: (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {partner.organisationName || 'N/A'}
            </Typography>
          </Box>
        ),
        commission: (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#064d51' }}>
              {commissionPercentage}%
            </Typography>
            <Chip
              label={commissionType}
              size="small"
              variant="outlined"
              sx={{
                height: '22px',
                fontSize: '0.7rem',
                fontWeight: '600',
                borderColor: commissionType === 'PROFIT' ? 'success.main' : 'info.main',
                color: commissionType === 'PROFIT' ? 'success.main' : 'info.main',
              }}
            />
          </Box>
        ),
        kycStatus: (
          <Chip
            label={partner.kycStatus || 'PENDING'}
            size="small"
            sx={{
              backgroundColor: kycColors.bg,
              color: kycColors.color,
              fontSize: '0.75rem',
              fontWeight: 500
            }}
          />
        ),
        createdAt: moment.utc(partner.createdAt).local().format("MMM DD, YYYY"),
        status: (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={partner.status || 'PENDING'}
              size="small"
              sx={{
                backgroundColor: statusColors.bg,
                color: statusColors.color,
                fontSize: '0.75rem',
                fontWeight: 500
              }}
            />
            <Switch
              checked={isActive}
              color="success"
              size="small"
              onChange={() => {
                setStatusDialog({
                  open: true,
                  partnerId: partner.id,
                  newStatus: !isActive
                });
              }}
              disabled={statusChanging}
            />
          </Box>
        ),
        actions: (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => _navigateToDetailScreen(partner.id, partner)}
              title="View Details"
              sx={{ color: '#064d51' }}
            >
              <Icon>visibility</Icon>
            </IconButton>
            <IconButton
              size="small"
              onClick={() => _navigateToCommissionConfig(partner.organisationId)}
              title="Commission Config"
              sx={{ color: '#064d51' }}
            >
              <Icon>settings</Icon>
            </IconButton>
          </Box>
        ),
      };
    });

    setTableData({ ...tableData, rows });
  };

  // Fetch applications on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        await fetchApplications();
      } catch (err) {
        console.error('Failed to fetch channel partners:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [fetchApplications]);

  // Update table when applications change
  useEffect(() => {
    if (applications && applications.length > 0) {
      _generateTableRows(applications);
    } else {
      setTableData({ ...tableData, rows: [] });
    }
  }, [applications]);

  const _navigateToCreationScreen = () => {
    navigate("/ADM-ChannelPartners/adm-new-channel-partner");
  };

  const _navigateToDetailScreen = (partnerId, partnerData) => {
    navigate("/ADM-ChannelPartners/adm-channel-partner-detail", {
      state: { partnerId, partnerData },
    });
  };

  const _navigateToCommissionConfig = (partnerId) => {
    navigate("/ADM-ChannelPartners/adm-commission-config", {
      state: { partnerId },
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => {}}>
          Failed to load channel partners. Please try again.
        </Alert>
      )}

      {/* Status Change Confirmation Dialog */}
      <Dialog
        open={statusDialog.open}
        onClose={() => setStatusDialog({ ...statusDialog, open: false })}
      >
        <DialogTitle>Confirm Status Change</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to set status to{" "}
            <strong>{statusDialog.newStatus ? "Active" : "Inactive"}</strong>{" "}
            for this channel partner?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setStatusDialog({ ...statusDialog, open: false })}
            color="inherit"
            disabled={statusChanging}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleStatusChange(statusDialog.partnerId, statusDialog.newStatus);
              setStatusDialog({ ...statusDialog, open: false });
            }}
            color="primary"
            autoFocus
            disabled={statusChanging}
          >
            {statusChanging ? 'Confirming...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Data Table */}
      {isLoading ? (
        <TableLoader />
      ) : (
        <DataTable
          table={tableData}
          showAddButton={true}
          addButtonOnClick={_navigateToCreationScreen}
          addButtonLabel="Add Channel Partner"
        />
      )}
    </Box>
  );
};

export default ChannelPartnerList;
